import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { api } from '../api.js';
import { useAuth } from '../auth.jsx';
import AuthLayout from '../components/AuthLayout.jsx';
import Field from '../components/Field.jsx';
import Alert from '../components/ui/Alert.jsx';
import useFieldErrors from '../utils/useFieldErrors.js';
import { checkOwnDomain, checkRequired, cleanDomain, cleanedHint } from '../utils/validate.js';

/**
 * "Your company": the first thing a new customer does, before any event. The email
 * domain is prefilled from their own address (unless it's Gmail, Outlook…).
 */
export default function Onboarding() {
  const { organisation, suggestedEmailDomain, refresh, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: organisation?.onboarded ? organisation.name : '',
    email_domain: suggestedEmailDomain || '',
    website: suggestedEmailDomain || '',
  });
  const fe = useFieldErrors();
  const { errors } = fe;
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (!organisation) return <Navigate to={isAdmin ? '/admin' : '/login'} replace />;
  if (organisation.onboarded) return <Navigate to="/company" replace />;

  const set = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); fe.clear(k); };
  // Leaving a domain field: say what's wrong, or tidy a pasted address into the domain
  const blurDomain = (k) => () => {
    if (!form[k].trim()) return;
    const problem = checkOwnDomain(form[k]);
    if (problem) fe.check(k, problem);
    else setForm((f) => ({ ...f, [k]: cleanDomain(f[k]) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const ok = fe.validate({
      name: checkRequired(form.name, 'your company’s name', { max: 200 }),
      email_domain: checkOwnDomain(form.email_domain, { required: false })
        || (!form.email_domain.trim() && !form.website.trim() ? 'Add the domain your company sends email from, or your website.' : ''),
      website: checkOwnDomain(form.website, { required: false }),
    });
    if (!ok) return;
    setBusy(true); setError('');
    try {
      await api(`/portal/organisations/${organisation.id}/onboarding`, { method: 'POST', body: form });
      await refresh();
      navigate('/setup', { replace: true });
    } catch (err) {
      setError(fe.fromServer(err));
      setBusy(false);
    }
  };

  return (
    <AuthLayout onSubmit={submit} wide>
      <h1>Your company</h1>
      <p className="muted">
        Most of what proves an email is really from you belongs to your company, not to one event — your domain,
        website, phone numbers, bank account. Set those up once and every event you run uses them.
      </p>
      {error && <Alert tone="danger">{error}</Alert>}
      <Field label="Company name" required value={form.name} onChange={set('name')} error={errors.name}
        placeholder="Techsummit Events Ltd" autoComplete="organization" maxLength={200} />
      <Field label="Email domain you send from" optional value={form.email_domain} onChange={set('email_domain')} onBlur={blurDomain('email_domain')}
        error={errors.email_domain} placeholder="techsummit.events" autoComplete="off" spellCheck={false} inputMode="url" autoCapitalize="none"
        hint={!errors.email_domain ? (cleanedHint(form.email_domain) || (suggestedEmailDomain ? 'Taken from your own email address. Change it if your company sends from another domain.' : 'The part after @ in your company’s email addresses.')) : undefined} />
      <Field label="Website" optional value={form.website} onChange={set('website')} onBlur={blurDomain('website')} error={errors.website}
        placeholder="techsummit.events" autoComplete="url" spellCheck={false} inputMode="url" autoCapitalize="none"
        hint={!errors.website ? (cleanedHint(form.website) || 'Just the domain is enough — we’ll check the whole site.') : undefined} />
      <p className="muted small">Add at least one of the two. Everything else — brands, more domains, phone numbers, bank details — goes on your Company page.
        We&rsquo;ll then ask you to prove each domain is yours, so nobody else can claim it.</p>
      <button type="submit" className="btn btn-primary btn-block" disabled={busy}>{busy ? 'Saving…' : 'Continue'}</button>
    </AuthLayout>
  );
}
